import { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  Input,
  Label,
  Badge,
  Alert,
  AlertDescription,
} from '@xanectech/ui/components';
import {
  Fingerprint,
  AlertCircle,
  CheckCircle,
  Smartphone,
  Laptop,
  Cloud,
  Shield,
  Copy,
} from 'lucide-react';
import { useToast } from '@xanectech/ui/lib';
import { createLogger } from '@xanectech/lib';
import { useAuth } from '../contexts/AuthContext';
import { isPlatformAuthenticatorAvailable } from '../services/WebAuthnService';
import { useWebAuthnRegistration } from '../hooks/useWebAuthnRegistration';

const logger = createLogger('Auth - WebAuthnSetupDialog');

interface WebAuthnSetupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete: () => void;
}

export function WebAuthnSetupDialog({
  open,
  onOpenChange,
  onComplete,
}: WebAuthnSetupDialogProps) {
  const { user } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null);
  const [deviceName, setDeviceName] = useState('');
  const [step, setStep] = useState<
    | 'intro'
    | 'type-selection'
    | 'naming'
    | 'authenticating'
    | 'recovery-key'
    | 'complete'
  >('intro');
  const [authenticatorType, setAuthenticatorType] = useState<
    'platform' | 'cross-platform'
  >('platform');
  const [recoveryKey, setRecoveryKey] = useState<string[]>([]);
  const [copiedRecoveryKey, setCopiedRecoveryKey] = useState(false);
  const {
    registerDevice,
    isRegistering,
    progress,
    error: registrationError,
  } = useWebAuthnRegistration();

  const { toast } = useToast();

  const checkAvailability = useCallback(async () => {
    try {
      const available = await isPlatformAuthenticatorAvailable();
      if (typeof available === 'boolean') {
        setIsAvailable(available);

        if (!available) {
          setError(
            'Your device does not support biometric authentication. Please use a device with Touch ID, Face ID, or Windows Hello.',
          );
        }
      } else {
        setIsAvailable(false);
        setError(
          'Unable to detect biometric authentication support on this device.',
        );
      }
    } catch {
      setIsAvailable(false);
      setError(
        'Unable to detect biometric authentication support on this device.',
      );
    }
  }, []);

  useEffect(() => {
    if (open) {
      void checkAvailability();
    }
  }, [open, checkAvailability]);

  const handleContinue = () => {
    if (!isAvailable) return;
    setStep('type-selection');
    setError(null);
  };

  const handleSelectType = (type: 'platform' | 'cross-platform') => {
    setAuthenticatorType(type);
    const defaultName = getDefaultDeviceName(type);
    setDeviceName(defaultName);
    setStep('naming');
    setError(null);
  };

  const getDefaultDeviceName = (type?: 'platform' | 'cross-platform') => {
    const userAgent = navigator.userAgent.toLowerCase();
    const deviceType = type || authenticatorType;

    if (deviceType === 'cross-platform') {
      return 'Synced Passkey';
    }

    if (userAgent.includes('mac')) return 'MacBook';
    if (userAgent.includes('iphone')) return 'iPhone';
    if (userAgent.includes('ipad')) return 'iPad';
    if (userAgent.includes('android')) return 'Android Device';
    if (userAgent.includes('windows')) return 'Windows PC';
    if (userAgent.includes('linux')) return 'Linux PC';

    return 'My Device';
  };

  const handleRegister = async () => {
    if (!user?.id || !deviceName.trim()) {
      setError('Please enter a device name');
      return;
    }

    setStep('authenticating');
    setError(null);

    try {
      const registration = await registerDevice({
        deviceName: deviceName.trim(),
        deviceType: 'primary',
        authenticatorType:
          authenticatorType === 'cross-platform' ? 'cross-device' : 'platform',
      });

      if (!registration.success) {
        setStep('naming');
        setError(registration.error || 'Failed to register biometric unlock.');
        return;
      }

      const resultRecoveryKey = registration.recoveryKey ?? [];

      if (resultRecoveryKey.length > 0) {
        setRecoveryKey(resultRecoveryKey);
        setStep('recovery-key');
      } else {
        setStep('complete');
      }

      toast({
        title: 'Biometric authentication enabled',
        description: `${deviceName} is now registered for secure sign-in and unlock.`,
        variant: 'success',
      });
    } catch (err: unknown) {
      setStep('naming');
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to register biometric authentication. Please try again.',
      );
    }
  };

  const handleCopyRecoveryKey = async () => {
    if (recoveryKey.length === 0) return;

    try {
      await navigator.clipboard.writeText(recoveryKey.join(' '));
      setCopiedRecoveryKey(true);
      setTimeout(() => setCopiedRecoveryKey(false), 2000);
    } catch (err) {
      logger.error('Failed to copy recovery key', {
        error: err instanceof Error ? err.message : String(err),
      });
    }
  };

  const resetDialog = () => {
    setStep('intro');
    setDeviceName('');
    setRecoveryKey([]);
    setCopiedRecoveryKey(false);
    setError(null);
  };

  const handleFinishSetup = () => {
    onComplete();
    onOpenChange(false);
    resetDialog();
  };

  const handleClose = () => {
    if (!isRegistering) {
      onOpenChange(false);
      setTimeout(resetDialog, 300);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className='sm:max-w-md'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <Fingerprint className='w-5 h-5 text-primary' />
            Enable Biometric Unlock
          </DialogTitle>
          <DialogDescription>
            {step === 'intro' &&
              'Use your fingerprint, face, or security key to unlock your encrypted data quickly and securely.'}
            {step === 'type-selection' &&
              'Choose how you want to set up biometric authentication.'}
            {step === 'naming' &&
              'Give this authenticator a name so you can identify it later.'}
            {step === 'authenticating' &&
              'Follow the prompt on your device to complete registration...'}
            {step === 'recovery-key' &&
              'Store your recovery key safely before finishing setup.'}
            {step === 'complete' &&
              'Biometric unlock is now configured for your account.'}
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-4 py-4'>
          {step === 'intro' && (
            <>
              {isAvailable === null && (
                <div className='flex items-center justify-center py-8'>
                  <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary' />
                </div>
              )}

              {isAvailable === true && (
                <>
                  <Alert className='border-primary/20 bg-primary/5'>
                    <CheckCircle className='h-4 w-4 text-primary' />
                    <AlertDescription className='text-xs'>
                      <strong>Biometric authentication detected!</strong> Your
                      device supports secure biometric unlock.
                    </AlertDescription>
                  </Alert>

                  <div className='bg-muted p-3 rounded-lg space-y-2 text-xs'>
                    <p className='font-medium'>How it works:</p>
                    <ul className='list-disc list-inside space-y-1 text-muted-foreground'>
                      <li>Your device securely stores your encryption key</li>
                      <li>Unlock with fingerprint, face, or security key</li>
                      <li>Never re-enter your password on this device</li>
                      <li>Hardware-backed security prevents XSS attacks</li>
                    </ul>
                  </div>

                  <div className='flex items-center gap-3 p-3 rounded-lg border bg-card'>
                    <Smartphone className='w-8 h-8 text-muted-foreground' />
                    <div className='flex-1 text-xs'>
                      <p className='font-medium'>Current Device</p>
                      <p className='text-muted-foreground'>
                        {getDefaultDeviceName()}
                      </p>
                    </div>
                  </div>
                </>
              )}

              {isAvailable === false && error && (
                <Alert variant='destructive'>
                  <AlertCircle className='h-4 w-4' />
                  <AlertDescription className='text-xs'>
                    {error}
                  </AlertDescription>
                </Alert>
              )}
            </>
          )}

          {step === 'type-selection' && (
            <div className='space-y-4'>
              <div
                className='p-4 rounded-lg border-2 border-border hover:border-primary cursor-pointer transition-colors'
                onClick={() => handleSelectType('platform')}
              >
                <div className='flex items-start gap-3'>
                  <Laptop className='h-8 w-8 text-primary mt-1' />
                  <div className='flex-1'>
                    <h3 className='font-semibold mb-1'>This Device Only</h3>
                    <p className='text-sm text-muted-foreground mb-2'>
                      Use Touch ID, Face ID, or Windows Hello on this device
                    </p>
                    <div className='flex items-center gap-2 text-xs'>
                      <Badge variant='secondary' className='text-xs'>
                        <Shield className='h-3 w-3 mr-1' />
                        Most Secure
                      </Badge>
                      <span className='text-muted-foreground'>
                        Biometric stays on device
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div
                className='p-4 rounded-lg border-2 border-border hover:border-primary cursor-pointer transition-colors'
                onClick={() => handleSelectType('cross-platform')}
              >
                <div className='flex items-start gap-3'>
                  <Cloud className='h-8 w-8 text-primary mt-1' />
                  <div className='flex-1'>
                    <h3 className='font-semibold mb-1'>
                      Passkey (Syncs Across Devices)
                    </h3>
                    <p className='text-sm text-muted-foreground mb-2'>
                      Use iCloud Keychain, Google Password Manager, or other
                      passkey providers
                    </p>
                    <div className='flex items-center gap-2 text-xs'>
                      <Badge variant='secondary' className='text-xs'>
                        <Smartphone className='h-3 w-3 mr-1' />
                        Multi-Device
                      </Badge>
                      <span className='text-muted-foreground'>
                        Syncs via your provider
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <Alert className='border-primary/20 bg-primary/5'>
                <CheckCircle className='h-4 w-4 text-primary' />
                <AlertDescription className='text-xs'>
                  You can register both types and use whichever is most
                  convenient at the time.
                </AlertDescription>
              </Alert>
            </div>
          )}

          {step === 'naming' && (
            <>
              <div className='space-y-2'>
                <Label htmlFor='device-name'>Device Name</Label>
                <Input
                  id='device-name'
                  type='text'
                  value={deviceName}
                  onChange={(e) => setDeviceName(e.target.value)}
                  placeholder='e.g., My MacBook Pro'
                  disabled={isRegistering}
                  autoFocus
                  maxLength={50}
                />
                <p className='text-xs text-muted-foreground'>
                  This helps you identify which device is registered.
                </p>
              </div>

              <Alert className='border-primary/20 bg-primary/5'>
                <Fingerprint className='h-4 w-4 text-primary' />
                <AlertDescription className='text-xs'>
                  Your biometric data never leaves your device and is not stored
                  on our servers.
                </AlertDescription>
              </Alert>
            </>
          )}

          {step === 'authenticating' && (
            <div className='flex flex-col items-center justify-center py-8 space-y-4'>
              <div className='relative'>
                <Fingerprint className='w-16 h-16 text-primary animate-pulse' />
              </div>
              <p className='text-sm text-center text-muted-foreground'>
                {progress ||
                  "Please authenticate using your device's biometric sensor..."}
              </p>
            </div>
          )}

          {step === 'recovery-key' && (
            <div className='space-y-4'>
              <Alert className='border-warning/30 bg-warning/5'>
                <AlertCircle className='h-4 w-4 text-warning' />
                <AlertDescription className='text-xs'>
                  Save this recovery key in a secure offline location. It is
                  required if you lose access to your registered devices.
                </AlertDescription>
              </Alert>

              <div className='rounded-md border bg-muted/40 p-3'>
                <div className='grid grid-cols-2 gap-2 text-xs sm:grid-cols-3'>
                  {recoveryKey.map((word, index) => (
                    <div
                      key={`${word}-${index}`}
                      className='rounded border bg-background px-2 py-1 font-mono'
                    >
                      <span className='mr-1 text-muted-foreground'>
                        {index + 1}.
                      </span>
                      {word}
                    </div>
                  ))}
                </div>
              </div>

              <Button
                type='button'
                variant='outline'
                className='w-full'
                onClick={() => void handleCopyRecoveryKey()}
              >
                <Copy className='h-4 w-4 mr-2' />
                {copiedRecoveryKey
                  ? 'Copied Recovery Key'
                  : 'Copy Recovery Key'}
              </Button>
            </div>
          )}

          {step === 'complete' && (
            <Alert className='border-primary/20 bg-primary/5'>
              <CheckCircle className='h-4 w-4 text-primary' />
              <AlertDescription className='text-xs'>
                Biometric authentication is active. You can now use passkeys for
                supported security flows.
              </AlertDescription>
            </Alert>
          )}

          {(error || registrationError) &&
            step !== 'intro' &&
            step !== 'recovery-key' &&
            step !== 'complete' && (
              <Alert variant='destructive'>
                <AlertCircle className='h-4 w-4' />
                <AlertDescription className='text-xs'>
                  {error || registrationError}
                </AlertDescription>
              </Alert>
            )}
        </div>

        <DialogFooter className='flex-col sm:flex-row gap-2'>
          <Button
            variant='outline'
            onClick={handleClose}
            disabled={isRegistering}
          >
            {step === 'intro'
              ? 'Maybe Later'
              : step === 'type-selection'
                ? 'Back'
                : 'Cancel'}
          </Button>

          {step === 'intro' && isAvailable && (
            <Button onClick={handleContinue} disabled={isRegistering}>
              Continue
            </Button>
          )}

          {step === 'type-selection' && (
            <Button variant='ghost' onClick={() => setStep('intro')}>
              Back
            </Button>
          )}

          {step === 'naming' && (
            <Button
              onClick={handleRegister}
              disabled={isRegistering || !deviceName.trim()}
            >
              {isRegistering ? 'Setting up...' : 'Enable Biometric Unlock'}
            </Button>
          )}

          {step === 'recovery-key' && (
            <Button onClick={handleFinishSetup}>
              I've Saved My Recovery Key
            </Button>
          )}

          {step === 'complete' && (
            <Button onClick={handleFinishSetup}>Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
